/*
  Books lesson alarms on the phone itself when this web app runs inside the ClassTutor phone app.
  Ported from Tazkiyah (tazkiyah/src/native/nativeReminders.ts) and adapted to one-off lesson times.

  Android: our own plugin (ClassTutorAlarm, in native/android) keeps the list and rings loudly,
           full screen over the lock screen, until Dismiss (or a minute passes), with a Snooze.
  iPhone:  alarms through Apple's AlarmKit (iOS 26+) for lessons in the next day, plus local
           notifications with sound for everything in the next two weeks. iPhones can't run code
           when a reminder is due, so the plan is re-booked each time the app opens.
  Website / installed web app: nothing here; the server sends web push and the in-app ringer rings.
*/
import { nativePlatform, nativePlugin } from "./nativeApp";
import type { PhoneAlarm } from "./lessonAlarms";

type Permission = "granted" | "denied" | "prompt" | "prompt-with-rationale";

export interface PhoneReminderStatus {
  platform: "ios" | "android";
  notifications: Permission;
  alarms: Permission | "unavailable";
  exactAlarms: boolean;
  fullScreen: boolean;
  booked: number;
}

interface ClassTutorAlarmPlugin {
  getStatus(): Promise<{ notifications: Permission; exactAlarms: boolean; fullScreen: boolean; count: number }>;
  requestNotifications(): Promise<{ notifications: Permission }>;
  openExactAlarmSettings(): Promise<void>;
  openFullScreenSettings(): Promise<void>;
  setAlarms(options: { alarms: PhoneAlarm[] }): Promise<{ scheduled: number; exactAlarms: boolean }>;
  preview(options: { sound: boolean; vibrate: boolean; title?: string; body?: string }): Promise<void>;
  stopPreview(): Promise<void>;
}

type AppleWeekdayName = "MONDAY" | "TUESDAY" | "WEDNESDAY" | "THURSDAY" | "FRIDAY" | "SATURDAY" | "SUNDAY";
interface AlarmKitPlugin {
  isAvailable(): Promise<{ available: boolean }>;
  checkPermissions(): Promise<{ alarms: Permission }>;
  requestPermissions(): Promise<{ alarms: Permission }>;
  createAlarm(options: { hour: number; minute: number; label?: string; days?: AppleWeekdayName[] }): Promise<{ id: string | null }>;
  getAlarms(): Promise<{ alarms: { id: string }[] }>;
  cancelAlarm(options: { id: string }): Promise<void>;
}

interface LocalNotificationsPlugin {
  checkPermissions(): Promise<{ display: Permission }>;
  requestPermissions(): Promise<{ display: Permission }>;
  getPending(): Promise<{ notifications: { id: number }[] }>;
  cancel(options: { notifications: { id: number }[] }): Promise<void>;
  schedule(options: { notifications: unknown[] }): Promise<unknown>;
}

const android = () => (nativePlatform() === "android" ? nativePlugin<ClassTutorAlarmPlugin>("ClassTutorAlarm") : null);
const alarmKit = () => (nativePlatform() === "ios" ? nativePlugin<AlarmKitPlugin>("Alarm") : null);
const iosNotifications = () => (nativePlatform() === "ios" ? nativePlugin<LocalNotificationsPlugin>("LocalNotifications") : null);

export const phoneCanRing = () => Boolean(android() || alarmKit());

export async function getPhoneReminderStatus(): Promise<PhoneReminderStatus | null> {
  const droid = android();
  if (droid) {
    const s = await droid.getStatus();
    return { platform: "android", notifications: s.notifications, alarms: "granted", exactAlarms: s.exactAlarms, fullScreen: s.fullScreen, booked: s.count };
  }
  const notes = iosNotifications();
  if (!notes) return null;
  const kit = alarmKit();
  const available = kit ? (await kit.isAvailable()).available : false;
  return {
    platform: "ios",
    notifications: (await notes.checkPermissions()).display,
    alarms: kit && available ? (await kit.checkPermissions()).alarms : "unavailable",
    exactAlarms: true,
    fullScreen: true,
    booked: (await notes.getPending()).notifications.length,
  };
}

export async function requestPhoneReminderPermissions(needAlarms: boolean): Promise<PhoneReminderStatus | null> {
  const droid = android();
  if (droid) { await droid.requestNotifications(); return getPhoneReminderStatus(); }
  const notes = iosNotifications();
  if (!notes) return null;
  await notes.requestPermissions();
  const kit = alarmKit();
  if (needAlarms && kit && (await kit.isAvailable()).available) await kit.requestPermissions();
  return getPhoneReminderStatus();
}

export async function openAndroidAlarmSettings(which: "exact" | "fullScreen"): Promise<void> {
  const droid = android();
  if (!droid) return;
  await (which === "exact" ? droid.openExactAlarmSettings() : droid.openFullScreenSettings());
}

export interface ApplyResult { alarms: number; notifications: number; warnings: string[] }

/** Replace every booked lesson alarm on this phone with this plan. */
export async function applyPhoneAlarms(alarms: PhoneAlarm[]): Promise<ApplyResult | null> {
  const platform = nativePlatform();
  if (!platform) return null;
  return platform === "android" ? applyAndroid(alarms) : applyIos(alarms);
}

export async function previewAlarm(sound: boolean, vibrate: boolean): Promise<boolean> {
  const droid = android();
  if (!droid) return false;
  await droid.preview({ sound, vibrate, title: "Maths starts in 10 minutes", body: "This is how your lesson alarm will sound." });
  return true;
}
export const stopAlarmPreview = async () => { await android()?.stopPreview(); };

async function applyAndroid(alarms: PhoneAlarm[]): Promise<ApplyResult> {
  const droid = android()!;
  const warnings: string[] = [];
  const status = await droid.getStatus();
  const anyAlarm = alarms.some((a) => a.mode !== "notification");
  if (alarms.length && status.notifications !== "granted") warnings.push("Allow notifications for ClassTutor so lesson reminders can show.");
  if (anyAlarm && !status.exactAlarms) warnings.push("Approve alarms for ClassTutor (\"Alarms & reminders\") so they ring on the minute.");
  if (anyAlarm && !status.fullScreen) warnings.push("Allow full-screen alarms for ClassTutor so they show when your phone is locked.");
  await droid.setAlarms({ alarms });
  return { alarms: alarms.filter((a) => a.mode !== "notification").length, notifications: alarms.filter((a) => a.mode !== "alarm").length, warnings };
}

async function applyIos(alarms: PhoneAlarm[]): Promise<ApplyResult> {
  const warnings: string[] = [];
  const notes = iosNotifications();
  const kit = alarmKit();
  let alarmCount = 0;
  const ringing = alarms.filter((a) => a.mode !== "notification");
  const kitReady = kit && (await kit.isAvailable()).available && (await kit.checkPermissions()).alarms === "granted";
  if (kit && kitReady) {
    const { alarms: existing } = await kit.getAlarms();
    await Promise.all(existing.map((a) => kit.cancelAlarm({ id: a.id })));
    // AlarmKit books "the next time it is HH:MM", so only the coming 24 hours are unambiguous.
    const dayAhead = Date.now() + 24 * 3600 * 1000;
    for (const a of ringing) {
      if (a.at > dayAhead) break;
      const d = new Date(a.at);
      await kit.createAlarm({ hour: d.getHours(), minute: d.getMinutes(), label: a.title });
      alarmCount++;
    }
  } else if (ringing.length) {
    warnings.push(kit && (await kit.isAvailable()).available
      ? "Allow alarms for ClassTutor in your iPhone's Settings so they can ring."
      : "Loud alarms need iOS 26 or later. Your reminders will come as notifications with sound instead.");
  }
  let noteCount = 0;
  if (notes) {
    const pending = await notes.getPending();
    if (pending.notifications.length) await notes.cancel({ notifications: pending.notifications.map((n) => ({ id: n.id })) });
    const list = alarms.filter((a) => a.mode !== "alarm" || !kitReady).slice(0, 60);
    if (list.length && (await notes.checkPermissions()).display !== "granted") warnings.push("Allow notifications for ClassTutor so lesson reminders can show.");
    await notes.schedule({
      notifications: list.map((a) => ({
        id: a.registerId % 2147483647,
        title: a.title,
        body: a.body,
        sound: a.sound ? "default" : undefined,
        extra: { path: a.path, registerId: a.registerId },
        schedule: { at: new Date(a.at), allowWhileIdle: true },
        threadIdentifier: "lessons",
      })),
    });
    noteCount = list.length;
  }
  return { alarms: alarmCount, notifications: noteCount, warnings };
}
